<script setup lang="ts">
import RTOverviewArticleTypesList from "~/components/RTOverview/RTOverviewArticleTypesList.vue";
import RTOverviewBrakgroundFeeProgram from "~/components/RTOverview/RTOverviewBrakgroundFeeProgram.vue";
import type { ISanitizeOptions } from "@frontiers-vue3/public-pages";
import type { IResearchTopic, IKeyword } from "~/types/index";

const sanitize = inject("sanitize") as (
  html: string,
  options?: ISanitizeOptions
) => string;

const { getIsSubmitManuscriptEnabled, currentResearchTopic } = useMain();
const researchTopic = computed<IResearchTopic>(() => currentResearchTopic);

const { showInstitutionsBlock, isResearchTopicImportantNoteVisible } =
  useEnvironmentConfigStore().getConfig();
const terminology = useTerminologyStore();
const desc = ref(
  sanitize(researchTopic.value.description, {
    ALLOWED_TAGS: ["b", "strong", "a", "br", "img", "i", "em", "sub", "sup"]
  })
);

const keywords = computed((): string[] => {
  if (!researchTopic.value.keywords) {
    return [];
  }
  return researchTopic.value.keywords.map((rtKey: IKeyword) => rtKey.title);
});

const displayFeeProgram = computed(() => {
  return showInstitutionsBlock && researchTopic.value.isOpenForSubmission;
});
</script>
<template>
  <section class="Overview">
    <h2 class="SectionTitle">About</h2>

    <!-- eslint-disable-next-line vue/no-v-html -- Content is sanitized on line 20 with allowed tags -->
    <p v-html="desc"></p>

    <!-- Keywords & Important Note -->
    <div class="Overview__title">
      <template v-if="keywords">
        <h3 class="BlockTitle">Keywords</h3>
        <ul class="Overview__keywordsList">
          <li
            v-for="keyword in keywords"
            :key="keyword">
            <p class="Overview__keywordsItem">
              {{ keyword }}
            </p>
          </li>
        </ul>
      </template>
      <p v-if="isResearchTopicImportantNoteVisible">
        <strong>Important note: </strong>
        All contributions to this
        {{ terminology.getTerm("researchTopic") }} must be within the scope of
        the section and journal to which they are submitted, as defined in their
        mission statements. {{ terminology.getTerm("tenantName") }} reserves the
        right to guide an out-of-scope manuscript to a more suitable section or
        journal at any stage of peer review.
      </p>
    </div>

    <!-- Article types -->
    <RTOverviewArticleTypesList v-if="getIsSubmitManuscriptEnabled" />

    <!-- Fee program -->
    <RTOverviewBrakgroundFeeProgram v-if="displayFeeProgram" />
  </section>
</template>
<style lang="scss">
.Overview {
  h2 {
    margin-top: var(--spacing-vertical-l);

    &:first-child {
      margin-top: 0;
    }
  }

  &__title {
    margin-top: spacing(4);
  }

  &__articleTypes {
    ul {
      padding-left: spacing(4);
      list-style-type: disc;
      column-gap: spacing(4);
      columns: 2;

      @media #{$break-sm} {
        columns: 1;
      }
    }
  }

  &__authorsCards {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    grid-column-gap: spacing(4);
    grid-row-gap: spacing(4);
    margin-bottom: spacing(4);

    @media #{$break-laptop} {
      max-width: none;
      grid-template-columns: repeat(2, 1fr);
    }

    @media #{$break-xs} {
      grid-template-columns: repeat(1, 1fr);
    }

    a {
      display: grid;
      height: 100%;
    }
  }

  &__keywordsList {
    margin: spacing(4) 0;
    display: flex;
    flex-wrap: wrap;
    gap: spacing(0.5);
    row-gap: spacing(1);
  }

  &__keywordsItem {
    @include f("label-sm-semibold");
    border: 1px solid $grey40;
    background: $grey10;
    padding: spacing(0.4) spacing(1);
    border-radius: spacing(2);
    white-space: nowrap;
  }

  .FeeProgram {
    margin: 56px 0;
  }

  p {
    -webkit-line-clamp: unset;
    line-clamp: unset;
    margin-bottom: 0;
  }

  a {
    color: var(--maincolor50);
    font-weight: 700;

    &:hover {
      color: var(--maincolor70);
    }
  }

  b {
    font-weight: 500;
  }

  strong {
    font-weight: 700;
  }

  img {
    height: auto;
  }
}
</style>
